/**
 * CustomerFormPage — Criação/edição de cliente (CRM).
 *
 * Padrão `UserFormPage`/`ShiftFormPage`: sticky header (voltar + título +
 * salvar) + seções em `FormSection`. Create e edit no mesmo componente.
 *
 * Documento (CPF/CNPJ): máscara client-side conforme `type` (11/14 dígitos)
 * em `@/shared/utils/documentMask.ts` — o backend normaliza e valida o tamanho de
 * qualquer forma (`digitsOnly` em `customers/validation.ts`). Conflito de
 * documento duplicado (409) é tratado localmente como erro do campo
 * `document` (não como toast genérico) — `customersService.createCustomer`/
 * `updateCustomer` usa `_handleErrorLocally` pra não duplicar com o toast do
 * interceptor global.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import type { CreateCustomerRequest, CustomerAddress, UpdateCustomerRequest } from '@troia-v3/types';

import { Breadcrumb, Button, FormSection, FormField, Input, Select } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { TagsChipInput } from '../components';
import { useCustomer, useCreateCustomer, useUpdateCustomer } from '../hooks';
import { formatDocumentInput, documentDigitsOnly, documentLabel } from '@/shared/utils/documentMask';

interface AddressFormState {
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface CustomerFormState {
  name: string;
  type: 'PF' | 'PJ';
  document: string;
  email: string;
  phone: string;
  website: string;
  status: 'active' | 'inactive';
  segment: string;
  domains: string[];
  tags: string[];
  address: AddressFormState;
}

function getDefaultFormData(): CustomerFormState {
  return {
    name: '',
    type: 'PJ',
    document: '',
    email: '',
    phone: '',
    website: '',
    status: 'active',
    segment: 'general',
    domains: [],
    tags: [],
    address: {
      street: '',
      number: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'BR',
    },
  };
}

const TYPE_OPTIONS = [
  { value: 'PJ', label: 'Pessoa Jurídica (PJ)' },
  { value: 'PF', label: 'Pessoa Física (PF)' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
];

export const CustomerFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: customer, isLoading, isError } = useCustomer(id, { enabled: isEditMode });
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();

  const [formData, setFormData] = useState<CustomerFormState>(getDefaultFormData());
  const [hasChanges, setHasChanges] = useState(!isEditMode);
  const [documentError, setDocumentError] = useState<string | null>(null);

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name,
        type: customer.type,
        document: customer.document ? formatDocumentInput(customer.document, customer.type) : '',
        email: customer.email || '',
        phone: customer.phone || '',
        website: customer.website || '',
        status: customer.status,
        segment: customer.segment,
        domains: customer.domains ?? [],
        tags: customer.tags ?? [],
        address: {
          street: customer.address?.street || '',
          number: customer.address?.number || '',
          complement: customer.address?.complement || '',
          district: customer.address?.district || '',
          city: customer.address?.city || '',
          state: customer.address?.state || '',
          zipCode: customer.address?.zipCode || '',
          country: customer.address?.country || 'BR',
        },
      });
      setHasChanges(false);
    }
  }, [customer]);

  const handleFieldChange = <K extends keyof CustomerFormState>(field: K, value: CustomerFormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTypeChange = (type: 'PF' | 'PJ') => {
    setFormData((prev) => ({ ...prev, type, document: formatDocumentInput(prev.document, type) }));
    setDocumentError(null);
    setHasChanges(true);
  };

  const handleDocumentChange = (value: string) => {
    setFormData((prev) => ({ ...prev, document: formatDocumentInput(value, prev.type) }));
    setDocumentError(null);
    setHasChanges(true);
  };

  const handleAddressChange = <K extends keyof AddressFormState>(field: K, value: AddressFormState[K]) => {
    setFormData((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
    setHasChanges(true);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Nome é obrigatório';
    if (formData.name.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';

    const docDigits = documentDigitsOnly(formData.document);
    if (docDigits.length > 0) {
      const expected = formData.type === 'PF' ? 11 : 14;
      if (docDigits.length !== expected) {
        return `${documentLabel(formData.type)} inválido — informe ${expected} dígitos`;
      }
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      return 'Email inválido';
    }

    const addr = formData.address;
    const hasAnyAddressField = [addr.street, addr.number, addr.district, addr.city, addr.state, addr.zipCode].some(
      (v) => v.trim(),
    );
    if (hasAnyAddressField) {
      if (!addr.street.trim()) return 'Endereço: rua é obrigatória quando o endereço é preenchido';
      if (!addr.number.trim()) return 'Endereço: número é obrigatório quando o endereço é preenchido';
      if (!addr.district.trim()) return 'Endereço: bairro é obrigatório quando o endereço é preenchido';
      if (!addr.city.trim()) return 'Endereço: cidade é obrigatória quando o endereço é preenchido';
      if (addr.state.trim().length !== 2) return 'Endereço: UF deve ter 2 letras (ex: SP)';
      if (!addr.zipCode.trim()) return 'Endereço: CEP é obrigatório quando o endereço é preenchido';
    }

    return null;
  };

  const buildAddressPayload = (): CustomerAddress | undefined => {
    const addr = formData.address;
    const hasAnyAddressField = [addr.street, addr.number, addr.district, addr.city, addr.state, addr.zipCode].some(
      (v) => v.trim(),
    );
    if (!hasAnyAddressField) return undefined;
    return {
      street: addr.street.trim(),
      number: addr.number.trim(),
      ...(addr.complement.trim() ? { complement: addr.complement.trim() } : {}),
      district: addr.district.trim(),
      city: addr.city.trim(),
      state: addr.state.trim().toUpperCase(),
      zipCode: addr.zipCode.trim(),
      country: addr.country.trim() || 'BR',
    };
  };

  const buildPayload = (): CreateCustomerRequest | UpdateCustomerRequest => {
    const website = formData.website.trim();
    const normalizedWebsite = website ? (website.startsWith('http') ? website : `https://${website}`) : '';
    const address = buildAddressPayload();

    return {
      name: formData.name.trim(),
      type: formData.type,
      ...(formData.document.trim() ? { document: formData.document.trim() } : {}),
      ...(formData.email.trim() ? { email: formData.email.trim() } : {}),
      ...(formData.phone.trim() ? { phone: formData.phone.trim() } : {}),
      ...(normalizedWebsite ? { website: normalizedWebsite } : {}),
      segment: formData.segment.trim() || 'general',
      domains: formData.domains,
      tags: formData.tags,
      status: formData.status,
      ...(address ? { address } : {}),
    };
  };

  const handleCancel = () => {
    navigate(isEditMode ? `/crm/customers/${id}` : '/crm/customers');
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setDocumentError(null);
    const payload = buildPayload();

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: id!, data: payload as UpdateCustomerRequest });
        setHasChanges(false);
        navigate(`/crm/customers/${id}`);
      } else {
        const created = await createMutation.mutateAsync(payload as CreateCustomerRequest);
        navigate(`/crm/customers/${created.id}`);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setDocumentError((error.response.data as { message?: string } | undefined)?.message || 'Documento já cadastrado');
        return;
      }
      const message =
        (axios.isAxiosError(error) && (error.response?.data as { message?: string } | undefined)?.message) ||
        'Erro ao salvar cliente';
      toast.error(message);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  if (isEditMode && (isError || !customer)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-lg" style={{ color: 'var(--theme-error-color)' }}>
          Erro ao carregar cliente
        </p>
        <Button onClick={() => navigate('/crm/customers')} variant="secondary">
          Voltar para a lista
        </Button>
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="customers-form-page">
      {/* Sticky header — breadcrumb + ações */}
      <div
        className="sticky top-0 z-10 shadow-sm"
        style={{ backgroundColor: 'var(--theme-card-background)', borderBottom: '1px solid var(--theme-card-border)' }}
      >
        <div className="px-4 h-14 flex items-center">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 w-full">
            {/* Breadcrumb (F25-31: shared — mobile = legenda condensada sob o título) */}
            <Breadcrumb
              items={[
                { label: 'CRM', onNavigate: () => navigate('/crm'), testid: 'customers-form-breadcrumb-crm' },
                { label: 'Clientes', onNavigate: () => navigate('/crm/customers'), testid: 'customers-form-breadcrumb-list' },
                { label: isEditMode && customer ? customer.name : 'Novo cliente', testid: 'customers-form-breadcrumb-current' },
              ]}
              mobileLegend="CRM · Cliente"
              data-testid="customers-form-breadcrumb"
            />

            {/* Ações desktop */}
            <div className="hidden sm:flex items-center gap-3 flex-shrink-0 col-start-3">
              {hasChanges && (
                <span className="text-sm" style={{ color: 'var(--theme-warning-color)' }}>
                  {isEditMode ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
                </span>
              )}
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                data-testid="customers-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
                data-testid="customers-form-save"
              >
                {!isSaving && <Icon name="check" className="w-4 h-4 mr-2" />}
                {isEditMode ? 'Salvar alterações' : 'Criar cliente'}
              </Button>
            </div>
            {/* Ação mobile: salvar como ícone-check (mockup .m-ibtn.save, sem Cancelar) */}
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="sm:hidden col-start-3 w-9 h-9 inline-flex items-center justify-center rounded-lg transition-opacity disabled:opacity-40"
              style={{ color: 'var(--theme-primary)' }}
              aria-label={isEditMode ? 'Salvar alterações' : 'Criar cliente'}
              data-testid="customers-form-save-mobile"
            >
              <Icon name="check" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* Dados */}
          <FormSection icon="briefcase" title="Dados" description="Identificação e contato do cliente">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Nome" required>
                <Input
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder={formData.type === 'PF' ? 'Ex: João da Silva' : 'Ex: Acme Comércio Ltda'}
                  data-testid="customers-form-name"
                />
              </FormField>
              <FormField label="Tipo">
                <Select
                  options={TYPE_OPTIONS}
                  value={formData.type}
                  onChange={(value) => handleTypeChange(value as 'PF' | 'PJ')}
                  data-testid="customers-form-type"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={documentLabel(formData.type)} error={documentError ?? undefined}>
                <Input
                  value={formData.document}
                  onChange={(e) => handleDocumentChange(e.target.value)}
                  placeholder={formData.type === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                  data-testid="customers-form-document"
                />
              </FormField>
              <FormField label="Status">
                <Select
                  options={STATUS_OPTIONS}
                  value={formData.status}
                  onChange={(value) => handleFieldChange('status', value as 'active' | 'inactive')}
                  data-testid="customers-form-status"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Email">
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder="contato@empresa.com.br"
                  data-testid="customers-form-email"
                />
              </FormField>
              <FormField label="Telefone">
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  placeholder="(11) 98765-4321"
                  data-testid="customers-form-phone"
                />
              </FormField>
            </div>

            {/* mockup: helper 12px muted ABAIXO do input */}
            <FormField label="Website">
              <Input
                value={formData.website}
                onChange={(e) => handleFieldChange('website', e.target.value)}
                placeholder="www.empresa.com.br"
                data-testid="customers-form-website"
              />
              <p className="text-xs mt-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                https:// é adicionado automaticamente se omitido
              </p>
            </FormField>
          </FormSection>

          {/* Endereço */}
          <FormSection
            icon="map-pin"
            title="Endereço"
            description="Opcional — se iniciar, preencha os campos obrigatórios juntos (rua, número, bairro, cidade, CEP e UF)"
          >
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-4">
              <FormField label="Rua">
                <Input
                  value={formData.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  placeholder="Av. Paulista"
                  data-testid="customers-form-address-street"
                />
              </FormField>
              <FormField label="Número">
                <Input
                  value={formData.address.number}
                  onChange={(e) => handleAddressChange('number', e.target.value)}
                  placeholder="1000"
                  data-testid="customers-form-address-number"
                />
              </FormField>
              <FormField label="Complemento">
                <Input
                  value={formData.address.complement}
                  onChange={(e) => handleAddressChange('complement', e.target.value)}
                  placeholder="Sala 42"
                  data-testid="customers-form-address-complement"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-4">
              <FormField label="Bairro">
                <Input
                  value={formData.address.district}
                  onChange={(e) => handleAddressChange('district', e.target.value)}
                  placeholder="Bela Vista"
                  data-testid="customers-form-address-district"
                />
              </FormField>
              <FormField label="Cidade">
                <Input
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder="São Paulo"
                  data-testid="customers-form-address-city"
                />
              </FormField>
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <FormField label="UF">
                  <Input
                    value={formData.address.state}
                    onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="SP"
                    maxLength={2}
                    data-testid="customers-form-address-state"
                  />
                </FormField>
                <FormField label="CEP">
                  <Input
                    value={formData.address.zipCode}
                    onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                    placeholder="01310-100"
                    data-testid="customers-form-address-zipcode"
                  />
                </FormField>
              </div>
            </div>
          </FormSection>

          {/* Classificação */}
          <FormSection icon="tag" title="Classificação" description="Segmento, domínios e tags do cliente">
            <FormField label="Segmento" description="Ex: Varejo, Atacado, Imobiliário">
              <Input
                value={formData.segment}
                onChange={(e) => handleFieldChange('segment', e.target.value)}
                placeholder="general"
                data-testid="customers-form-segment"
              />
            </FormField>

            <FormField label="Domínios de e-mail">
              <TagsChipInput
                values={formData.domains}
                onChange={(values) => handleFieldChange('domains', values)}
                placeholder="acme.com.br"
                normalize={(v) => v.trim().toLowerCase()}
                variant="neutral"
                data-testid="customers-form-domains"
              />
              {/* `.hint` do mockup: ABAIXO dos chips, não acima */}
              <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
                Contatos que enviarem mensagem de um desses domínios são auto-associados a este cliente (ex: acme.com.br)
              </p>
            </FormField>

            <FormField label="Tags">
              <TagsChipInput
                values={formData.tags}
                onChange={(values) => handleFieldChange('tags', values)}
                placeholder="Adicionar tag..."
                data-testid="customers-form-tags"
              />
            </FormField>
          </FormSection>
        </div>
      </div>
    </div>
  );
};

CustomerFormPage.displayName = 'CustomerFormPage';
